import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  Input,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { AkitaPaymentQuery } from '@app/akita/api/payments/state/payment.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { environment } from '@environments/environment';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { map, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { TamaraPaymentOption } from '../../models/tamara-payment-option.model';
import { TamaraSupportedInstallment } from '../../models/tamara-supported-installment.model';
import {
  TamaraActionsService,
  /* TAMARA_MAX_ALLOWED_AMOUNT, */
  TAMARA_SUPPORTED_CURRENCIES,
} from '../../services/tamara-actions.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { CommonModule } from '@angular/common';

import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { logPaymentInfoCardClicked } from '@app/core/services/google-analytics.lazy';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';

@Component({
  selector: 'popsy-tamara-summary',
  templateUrl: './tamara-summary.template.html',
  styleUrls: ['./tamara-summary.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslocoModule,
  ],
})
export class TamaraSummaryComponent implements OnDestroy, OnChanges, AfterViewInit {
  @Input() public productId?: string | null;
  @Input() public amount?: number | null;
  @Input() public currency?: string | null;
  @Input() public country?: string | null;
  @Input() public mobileMode?: boolean | null;
  @Input() public mode?: 'paylater' | 'installment' | 'pay-next-month' | null;
  @Input() public source?: 'product' | 'cart' | null;

  public readonly lessThan$: Observable<ScreenSizeInfoState>;
  public readonly rtl$: Observable<boolean>;

  public isViewInitialized: boolean;
  public isTamaraSupported: boolean;

  public readonly widgetLocale$: Observable<string>;
  public installments$: Observable<number | null | undefined>;
  public installmentOption$: Observable<TamaraSupportedInstallment | null>;
  public tamaraPaymentOptions$: Observable<Array<TamaraPaymentOption>>;

  private tamaraSessionSubscription?: Subscription | null;
  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly tamaraActionsService: TamaraActionsService,
    private readonly akitaPaymentQuery: AkitaPaymentQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly translateService: TranslocoService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly analytics: GoogleAnalyticsService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();
    this.isViewInitialized = false;
    this.isTamaraSupported = false;
    this.monitorTamaraSession();
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.rtl$ = this.akitaRouterQuery.selectIsRtlLayout();

    this.tamaraPaymentOptions$ = this.akitaPaymentQuery.selectTamaraPaymentOptions(
      this.country
    );
    this.installments$ = this.tamaraPaymentOptions$.pipe(
      map(this.parseInstallmentsCount.bind(this))
    );
    this.installmentOption$ = this.tamaraPaymentOptions$.pipe(
      map(this.parseInstallmentOptions.bind(this))
    );
    this.widgetLocale$ = this.translateService.langChanges$.pipe(
      map((activeLang: string) => {
        let lang = 'en';
        if (`${activeLang || ''}`.toLowerCase() === 'ar') {
          lang = 'ar';
        }
        return lang;
      })
    );
  }
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.productId || changes?.currency || changes?.amount || changes?.source) {
      this.monitorTamaraSession();
      this.displayTamaraSummary();
    }

    if (changes?.country) {
      this.tamaraPaymentOptions$ = this.akitaPaymentQuery.selectTamaraPaymentOptions(
        this.country
      );
      this.installments$ = this.tamaraPaymentOptions$.pipe(
        map(this.parseInstallmentsCount.bind(this))
      );
      this.installmentOption$ = this.tamaraPaymentOptions$.pipe(
        map(this.parseInstallmentOptions.bind(this))
      );
    }
  }

  public ngAfterViewInit(): void {
    this.isViewInitialized = true;
    this.displayTamaraSummary();
  }

  private parseInstallmentsCount(
    options?: Array<TamaraPaymentOption> | null
  ): number | null | undefined {
    for (const option of options || []) {
      if (
        option.name === 'PAY_BY_INSTALMENTS' &&
        (option.supportedInstalments || []).length > 0
      ) {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return 4; // || option.supportedInstalments[0].instalments || undefined;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return 4;
  }

  private parseInstallmentOptions(
    options?: Array<TamaraPaymentOption> | null
  ): TamaraSupportedInstallment | null {
    for (const option of options || []) {
      if (
        option.name === 'PAY_BY_INSTALMENTS' &&
        (option.supportedInstalments || []).length > 0
      ) {
        return option.supportedInstalments[0] || null;
      }
    }

    return null;
  }

  private monitorTamaraSession(): void {
    if (this.tamaraSessionSubscription) {
      this.tamaraSessionSubscription.unsubscribe();
    }

    this.tamaraSessionSubscription = this.akitaPaymentQuery
      .selectTamaraPaymentOptions(this.country)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          this.displayTamaraSummary();
        },
        error: () => {},
      });
  }

  private displayTamaraSummary(): void {
    if (this.isViewInitialized) {
      this.isTamaraSupported = TAMARA_SUPPORTED_CURRENCIES.includes(
        `${this.currency || ''}`.toUpperCase()
      );

      if (this.currency && this.amount && this.isTamaraSupported) {
        let lang = 'en';
        if (`${this.translateService.getActiveLang() || ''}`.toLowerCase() === 'ar') {
          lang = 'ar';
        }

        this.subscriptions.add(
          this.tamaraActionsService
            .displayTamaraSummary({
              lang: lang,
              currency: this.currency,
              countryCode: this.country || 'SA',
              publicKey:
                this.akitaPaymentQuery.tamaraPublicKey || environment.tamara.pubKey,
              // eslint-disable-next-line @typescript-eslint/no-magic-numbers
              badgeRatio: 1,
            })
            .pipe(takeUntil(this.destroySubject))
            .subscribe({
              next: () => {},
              error: () => {},
            })
        );
      }
    }
  }

  public logClickEvent(): void {
    const trigger =
      this.source === 'product' ? 'Tamara-product-page' : 'Tamara-checkout-page';
    logPaymentInfoCardClicked(this.analytics.logEventWrapper, trigger);
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    if (this.tamaraSessionSubscription) {
      this.tamaraSessionSubscription.unsubscribe();
    }
  }
}
